import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  UnitTransferScopeEnumType,
  UnitTransferUnitsLocationEnumType,
} from '__generated__/graphql';
import { FC, useEffect, useRef, useState } from 'react';

import DetailsPanel from 'components/DetailsPanel';
import ErrorMessage from 'components/ErrorMessage';
import SectionCard from 'components/SectionCard';

import Alert from 'primitives/Alert';
import LoadingIndicator from 'primitives/LoadingIndicator';

import unitTransferReasonToReadable from 'utils/enums/unit-transfer-reason-to-readable';
import { fixToTwoLocalPrice } from 'utils/format-helper';

import { ScopeStepData } from './StepScope';
import { TransfereeStepData } from './StepTransferee';
import { UnitsLocationStepData } from './StepUnitsLocation';

const CREATE_UNIT_TRANSFER_REQUEST_MUTATION = gql(`
  mutation CreateUnitTransferRequestForReview(
    $transferorInvestmentEntityId: ID!
    $transfereeUserId: ID!
    $reason: UnitTransferReasonEnumType!
    $reasonDescription: String
    $relationship: String
    $scope: UnitTransferScopeEnumType!
    $closingIds: [ID!]
    $unitsLocation: UnitTransferUnitsLocationEnumType!
  ) {
    createUnitTransferRequest(
      transferorInvestmentEntityId: $transferorInvestmentEntityId
      transfereeUserId: $transfereeUserId
      reason: $reason
      reasonDescription: $reasonDescription
      relationship: $relationship
      scope: $scope
      closingIds: $closingIds
      unitsLocation: $unitsLocation
    ) {
      id
      schemes {
        id
        schemeName
        isin
        numberOfUnits
        investmentAmount
      }
    }
  }
`);

const SUBMIT_UNIT_TRANSFER_REQUEST_MUTATION = gql(`
  mutation SubmitUnitTransferRequestForReview($id: ID!) {
    submitUnitTransferRequest(id: $id) {
      id
      status
    }
  }
`);

type ReviewStepFormData = ScopeStepData & TransfereeStepData & UnitsLocationStepData;

const StepReview: FC<{
  data: ReviewStepFormData;
  draftRequestId: string | null;
  onDraftCreated: (id: string) => void;
  onReadyChange: (ready: boolean) => void;
  onSubmitted: (id: string) => void;
  buttonRef: any;
}> = ({ data, draftRequestId, onDraftCreated, onReadyChange, onSubmitted, buttonRef }) => {
  const [schemes, setSchemes] = useState<
    {
      id: string;
      schemeName: string;
      isin?: string | null;
      numberOfUnits: number;
      investmentAmount: number;
    }[]
  >([]);
  const createAttempted = useRef(false);

  const [createRequest, { loading: creating, error: createError }] = useMutation(
    CREATE_UNIT_TRANSFER_REQUEST_MUTATION
  );
  const [submitRequest, { loading: submitting, error: submitError }] = useMutation(
    SUBMIT_UNIT_TRANSFER_REQUEST_MUTATION
  );

  useEffect(() => {
    if (draftRequestId || createAttempted.current) return;
    createAttempted.current = true;

    createRequest({
      variables: {
        transferorInvestmentEntityId: data.transferorInvestmentEntityId,
        transfereeUserId: data.transfereeUserId,
        reason: data.reason as any,
        reasonDescription: data.reasonDescription || undefined,
        relationship: data.relationship || undefined,
        scope: data.scope as any,
        closingIds:
          data.scope === UnitTransferScopeEnumType.AllUnitsSelectedSchemes
            ? data.closingIds
            : undefined,
        unitsLocation: data.unitsLocation as any,
      },
    })
      .then(result => {
        if (result.data?.createUnitTransferRequest) {
          setSchemes(result.data.createUnitTransferRequest.schemes);
          onDraftCreated(result.data.createUnitTransferRequest.id);
        }
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftRequestId]);

  // Nothing can be submitted until the draft exists — if the create failed (BUS_060, a network
  // error) the button must be visibly dead rather than silently doing nothing.
  useEffect(() => {
    onReadyChange(!!draftRequestId);
  }, [draftRequestId, onReadyChange]);

  useEffect(() => {
    if (buttonRef) {
      buttonRef.current = {
        submit: () => {
          if (!draftRequestId) return;
          submitRequest({ variables: { id: draftRequestId } }).then(result => {
            if (result.data?.submitUnitTransferRequest) {
              onSubmitted(result.data.submitUnitTransferRequest.id);
            }
          });
        },
      };
    }
  }, [buttonRef, draftRequestId, submitRequest, onSubmitted]);

  if (creating && !draftRequestId) {
    return (
      <div>
        <p className="text-sm text-gray-600 mb-4">
          Creating your request and pulling your holdings…
        </p>
        <LoadingIndicator />
      </div>
    );
  }

  if (createError) {
    return <ErrorMessage error={createError} type="alert" />;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Review the details below. Once submitted, the transferee will be asked to acknowledge this
        request before it goes to our team for review.
      </p>

      <DetailsPanel
        title="Review your request"
        displayHorizontally
        data={[
          { label: 'Transferring from', value: data.transferorInvestmentEntityName },
          { label: 'Reason', value: unitTransferReasonToReadable(data.reason as any) },
          { label: 'Description', value: data.reasonDescription },
          { label: 'Relationship', value: data.relationship },
          {
            label: 'Transferee',
            value: `${data.transfereeName} (${data.transfereeMaskedEmail})`,
          },
          {
            label: 'Scope',
            value:
              data.scope === UnitTransferScopeEnumType.AllUnitsSelectedSchemes
                ? 'All units in selected schemes'
                : 'All units across all schemes',
          },
          {
            label: 'Units currently held in',
            value:
              data.unitsLocation === UnitTransferUnitsLocationEnumType.AlTrustEscrow
                ? 'AngelList India Trust escrow account'
                : 'Own demat account',
          },
        ]}
      />

      {schemes.length > 0 ? (
        <SectionCard
          title="Holdings snapshotted for this transfer"
          bodyClassName="px-4 py-3 sm:p-4 overflow-x-auto"
        >
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Scheme</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">ISIN</th>
                <th className="px-3 py-2 text-right font-medium text-gray-500">Units</th>
                <th className="px-3 py-2 text-right font-medium text-gray-500">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {schemes.map(scheme => (
                <tr key={scheme.id}>
                  <td className="px-3 py-2 text-gray-900">{scheme.schemeName}</td>
                  <td className="px-3 py-2 text-gray-500">{scheme.isin || '-'}</td>
                  <td className="px-3 py-2 text-right text-gray-900">{scheme.numberOfUnits}</td>
                  <td className="px-3 py-2 text-right text-gray-900">
                    ₹ {fixToTwoLocalPrice(scheme.investmentAmount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      ) : null}

      {draftRequestId ? (
        <Alert
          type="warning"
          title="A draft of this request has been saved"
          message="It now appears in your Unit Transfers list. You can only have one transfer request in flight at a time — if you leave without submitting this one, withdraw the draft from its tracker page before starting another."
        />
      ) : null}

      {submitError ? <ErrorMessage error={submitError} type="alert" /> : null}
      {submitting ? <LoadingIndicator /> : null}
    </div>
  );
};

export default StepReview;
